import {
  DefaultButton,
  IconButton,
  ITextProps,
  IToggleProps,
  mergeStyles,
  mergeStyleSets,
  ResponsiveMode,
  Stack,
  Text,
  Theme,
  Toggle,
  useTheme,
} from "@fluentui/react";
import React, { useEffect } from "react";
import { useCookies } from "react-cookie";
import { connect, ConnectedProps } from "react-redux";

import { SELECTED_THEME_COOKIE_NAME } from "../constants/constants";
import { useResponsiveWindow } from "../hooks/useResponsiveWindow";
import { RootState } from "../redux/setupStore";
import { setTheme, showNavigationPanel } from "../redux/slices/appSlice";
import { currentEnvironment } from "../utils/environment";
import { generateFeedbackUrl } from "../utils/TypescriptUtils";

const getStyles = (theme: Theme) =>
  mergeStyleSets({
    header: {
      backgroundColor: theme.palette.neutralQuaternaryAlt,
      padding: 10,
      width: "100%",
    },
    text: {
      color: theme.palette.neutralDark,
    },
    toggle: {
      margin: 0,
    },
  });

const Header: React.FC<IReduxProps> = ({
  currentTheme,
  fullName,
  firstName,
  navigationPanelIsVisible,
  setTheme,
  showNavigationPanel,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { isLargeWindow, isSmallWindow, currentWindowSize } = useResponsiveWindow();
  const [cookies, setCookie] = useCookies([SELECTED_THEME_COOKIE_NAME]);

  // On initial load, load the "theme" cookie and set the theme if one is specified
  useEffect(() => {
    if (cookies.theme) setTheme(cookies.theme);
  }, []);

  // each time the currentTheme changes, update the cookie
  useEffect(() => {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 100);
    setCookie("theme", currentTheme, { expires: expirationDate });
  }, [currentTheme, setCookie]);

  const StyledText: React.FC<ITextProps> = ({ children, ...props }) => (
    <Text {...props} className={styles.text}>
      {children}
    </Text>
  );

  const handleToggleTheme: IToggleProps["onChange"] = (_, checked) => {
    setTheme(checked ? "Dark" : "Default");
  };

  /**
   *
   * @returns page title
   */
  const getPageTitle = (): string => {
    switch (currentWindowSize) {
      case ResponsiveMode.small:
      case ResponsiveMode.medium:
        return "TDS";
      case ResponsiveMode.large:
        return "TDS Web";
      default:
        return "Topology Deployment Service (TDS)";
    }
  };

  const name = isLargeWindow ? fullName : firstName;

  const navMenuButtonStyles = mergeStyles({
    height: 50,
    width: 50,
    fontSize: "20px !important",
  });

  return (
    <header>
      <Stack
        horizontal
        horizontalAlign={isLargeWindow ? "space-evenly" : "space-between"}
        verticalAlign="end"
        className={styles.header}
      >
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
          {isSmallWindow && (
            <IconButton
              title={navigationPanelIsVisible ? "Collapse navigation menu" : "Expand navigation menu"}
              aria-haspopup={true}
              aria-expanded={navigationPanelIsVisible}
              iconProps={{ iconName: "GlobalNavButton", className: navMenuButtonStyles }}
              onClick={() => showNavigationPanel()}
            />
          )}
          <StyledText variant="xxLargePlus">{getPageTitle()}</StyledText>
        </Stack>

        <Stack horizontal tokens={{ childrenGap: 14 }} verticalAlign="center">
          <StyledText variant="medium">site: {currentEnvironment().display}</StyledText>
          <StyledText variant="medium">Hello, {name}</StyledText>
          <Toggle
            className={styles.toggle}
            checked={currentTheme === "Dark"}
            onText="Dark"
            offText="Light"
            onChange={handleToggleTheme}
          />
          <DefaultButton onClick={() => window.open(generateFeedbackUrl(fullName))}>Feedback</DefaultButton>
        </Stack>
      </Stack>
    </header>
  );
};

export const mapStateToProps = (state: RootState) => {
  const firstName = state.appState.user.name.split(" ")[0];

  return {
    currentTheme: state.appState.theme,
    fullName: state.appState.user.name,
    firstName,
    navigationPanelIsVisible: state.appState.showNavigationPanel,
  };
};

export const mapDispatchToProps = {
  setTheme,
  showNavigationPanel,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type IReduxProps = ConnectedProps<typeof connector>;

export default connector(Header);
